import React from "react";
import { Badge } from "components/ui/badge";
import { SectionCard, DefinitionList, FlagPill, StateBadge, Yes } from "./components";

// Renders the grouped "Formatted" view of a live event's status.
function StatusView({ formatted }) {
    if (!formatted || Object.keys(formatted).length === 0) {
        return <p className="mt-4 text-sm text-muted-foreground">No event status to display.</p>;
    }

    const { overview, timeline, encoders, mediaConnect, origin, audio, playoutControl, policies } =
        formatted;

    return (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {overview && <OverviewCard overview={overview} />}
            {timeline && <TimelineCard timeline={timeline} />}
            {policies && <PoliciesCard policies={policies} />}
            {encoders && <EncodersCard encoders={encoders} />}
            {mediaConnect && <MediaConnectCard mediaConnect={mediaConnect} />}
            {origin && <OriginCard origin={origin} />}
            {audio && <AudioCard audio={audio} />}
            {playoutControl && <PlayoutControlCard playout={playoutControl} />}
        </div>
    );
}

function OverviewCard({ overview }) {
    return (
        <SectionCard title="Overview">
            <DefinitionList
                items={[
                    { label: "Description", value: overview.description },
                    { label: "Channel", value: overview.channelId, mono: true },
                    { label: "Version", value: overview.version, mono: true },
                    { label: "Day", value: overview.day, mono: true },
                    {
                        label: "Passed starting gate",
                        value: overview.passedStartingGate ? <Yes /> : <Badge variant="outline">No</Badge>,
                    },
                    { label: "Language mapping", value: overview.languageMappingName, mono: true },
                    {
                        label: "Features",
                        value: overview.features?.length ? (
                            <ChipRow>
                                {overview.features.map((feature) => (
                                    <Badge key={feature} variant="secondary">
                                        {feature}
                                    </Badge>
                                ))}
                            </ChipRow>
                        ) : null,
                    },
                ]}
            />
        </SectionCard>
    );
}

function TimelineCard({ timeline }) {
    return (
        <SectionCard title="Timeline">
            <DefinitionList
                items={[
                    { label: "Stream start", value: timeline.streamStart, mono: true },
                    { label: "Stream end", value: timeline.streamEnd, mono: true },
                    { label: "Duration", value: timeline.duration },
                    { label: "Last update", value: timeline.lastUpdate, mono: true },
                    { label: "Time-to-live", value: timeline.timeToLive, mono: true },
                    {
                        label: "DVR window",
                        value: timeline.dvrWindow != null ? `${timeline.dvrWindow} s` : null,
                    },
                ]}
            />
        </SectionCard>
    );
}

function EncodersCard({ encoders }) {
    const summary = [
        encoders.encoderType ? capitalize(encoders.encoderType) : null,
        encoders.expected != null ? `${encoders.present} of ${encoders.expected} expected` : `${encoders.present}`,
        encoders.allRunning ? "all RUNNING" : "check states",
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <SectionCard title="Encoders" count={summary} className="lg:col-span-2">
            <div className="space-y-2.5">
                {encoders.list.map((encoder, index) => (
                    <EncoderBlock key={encoder.encoderId || index} encoder={encoder} index={index} />
                ))}
                {encoders.list.length === 0 && (
                    <p className="text-sm text-muted-foreground">No encoders reported.</p>
                )}
            </div>
        </SectionCard>
    );
}

function EncoderBlock({ encoder, index }) {
    return (
        <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="mb-2.5 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">Encoder {index + 1}</span>
                {encoder.label && <Badge variant="outline">{encoder.label}</Badge>}
                {encoder.isLive && <Badge variant="default">Live</Badge>}
                <StateBadge state={encoder.state} />
            </div>
            <DefinitionList
                items={[
                    { label: "Channel name", value: encoder.channelName, mono: true },
                    { label: "Channel ID", value: encoder.channelId, mono: true },
                    { label: "Encoder ID", value: encoder.encoderId, mono: true },
                    {
                        label: "Egress",
                        value: encoder.egress?.length ? encoder.egress.join(", ") : null,
                        mono: true,
                    },
                ]}
            />
            {encoder.inputs?.length > 0 && (
                <>
                    <SubLabel>Inputs</SubLabel>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                        {encoder.inputs.map((input, inputIndex) => (
                            <div
                                key={inputIndex}
                                className="rounded-md border border-border bg-card p-2.5"
                            >
                                <div className="mb-1 flex items-center gap-1.5">
                                    <Badge variant={input.role === "Primary" ? "default" : "secondary"}>
                                        {input.role}
                                    </Badge>
                                    {input.flowName && (
                                        <span className="font-mono text-[12px] text-muted-foreground">
                                            {input.flowName}
                                        </span>
                                    )}
                                </div>
                                <DefinitionList
                                    items={[
                                        { label: "Name", value: input.name, mono: true },
                                        { label: "Input ID", value: input.inputId, mono: true },
                                    ]}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function MediaConnectCard({ mediaConnect }) {
    const flowCount = mediaConnect.flows?.length || 0;
    return (
        <SectionCard
            title="MediaConnect"
            count={flowCount ? `${flowCount} flows` : null}
            className="lg:col-span-2"
        >
            {mediaConnect.connectEventId && (
                <div className="mb-3">
                    <DefinitionList
                        items={[
                            { label: "Connect event ID", value: mediaConnect.connectEventId, mono: true },
                        ]}
                    />
                </div>
            )}
            <div className="grid gap-4 lg:grid-cols-2">
                {mediaConnect.flows.map((flow, index) => (
                    <div key={index} className="rounded-lg border border-border bg-muted/30 p-3">
                        <div className="mb-2 flex items-center gap-2">
                            <Badge variant={flow.role === "Primary" ? "default" : "secondary"}>
                                {flow.role}
                            </Badge>
                            {flow.flowName && (
                                <span className="font-mono text-[12px] text-muted-foreground">
                                    {flow.flowName}
                                </span>
                            )}
                        </div>
                        <DefinitionList
                            items={[
                                { label: "Flow ID", value: flow.flowId, mono: true },
                                { label: "Region", value: flow.region, mono: true },
                            ]}
                        />
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

function OriginCard({ origin }) {
    const instanceCount = origin.instances?.length || 0;
    const summary = [
        `${instanceCount} instance${instanceCount === 1 ? "" : "s"}`,
        origin.originsPerEvent != null ? `${origin.originsPerEvent} per event` : null,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <SectionCard title="Origin" count={summary} className="lg:col-span-2">
            <div className="mb-3">
                <DefinitionList
                    items={[
                        { label: "Load factor", value: origin.loadFactor },
                        { label: "ASG", value: origin.asg, mono: true },
                        {
                            label: "Skip USP DNS creation",
                            value:
                                origin.skipUspDnsCreation != null ? (
                                    <Badge variant="outline">
                                        {origin.skipUspDnsCreation ? "Yes" : "No"}
                                    </Badge>
                                ) : null,
                        },
                    ]}
                />
            </div>
            <div className="space-y-2.5">
                {origin.instances.map((instance, index) => (
                    <div key={instance.id || index} className="rounded-lg border border-border bg-muted/30 p-3">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <Badge variant="secondary">{instance.id}</Badge>
                            {instance.az && <Badge variant="outline">{instance.az}</Badge>}
                        </div>
                        <DefinitionList
                            items={[
                                { label: "Public IP", value: instance.publicIp, mono: true },
                                { label: "Private IP", value: instance.privateIp, mono: true },
                                { label: "Endpoint", value: instance.endpoint, mono: true },
                            ]}
                        />
                        {instance.ingest && Object.keys(instance.ingest).length > 0 && (
                            <>
                                <SubLabel>Ingest URLs (public)</SubLabel>
                                <div className="flex flex-col gap-1">
                                    {Object.entries(instance.ingest).map(([label, url]) => (
                                        <a
                                            key={label}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="break-all text-[12px] text-primary hover:underline"
                                        >
                                            {label} — {url}
                                        </a>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

function AudioCard({ audio }) {
    const outlets = Object.keys(audio.outlets || {});
    return (
        <SectionCard
            title="Audio"
            count={`${audio.output.length} output track${audio.output.length === 1 ? "" : "s"}`}
        >
            <SubLabel className="mt-0">Output audio</SubLabel>
            <ChipRow>
                {audio.output.map((track) => (
                    <Badge key={track} variant="default">
                        {track}
                    </Badge>
                ))}
            </ChipRow>
            {outlets.length > 0 && (
                <>
                    <SubLabel>Source audio</SubLabel>
                    <p className="m-0 text-[12.5px] text-muted-foreground">
                        {outlets.length} outlet{outlets.length === 1 ? "" : "s"} ({outlets.join(", ")}).
                    </p>
                </>
            )}
        </SectionCard>
    );
}

function PlayoutControlCard({ playout }) {
    return (
        <SectionCard title="Playout Control">
            {playout.flags?.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                    {playout.flags.map((flag) => (
                        <FlagPill key={flag.label} label={flag.label} value={flag.value} />
                    ))}
                </div>
            )}
            <DefinitionList
                items={[
                    {
                        label: "USP profiles",
                        value: playout.uspProfiles ? (
                            <Badge variant="outline">{playout.uspProfiles}</Badge>
                        ) : null,
                    },
                    {
                        label: "DVR window",
                        value: playout.dvrWindow != null ? `${playout.dvrWindow} s` : null,
                    },
                    {
                        label: "Suppress break IDs",
                        value: <BadgeList items={playout.suppressBreakIds} variant="secondary" />,
                    },
                    {
                        label: "Passthrough break IDs",
                        value: <BadgeList items={playout.passthroughBreakIds} variant="secondary" />,
                    },
                    {
                        label: "Allowed break types",
                        value: <BadgeList items={playout.allowedBreakTypes} variant="outline" />,
                    },
                ]}
            />
        </SectionCard>
    );
}

function PoliciesCard({ policies }) {
    return (
        <SectionCard title="Policy Statements" className="lg:col-span-2">
            {policies.flags?.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                    {policies.flags.map((flag) => (
                        <FlagPill key={flag.label} label={flag.label} value={flag.value} />
                    ))}
                </div>
            )}
            <div className="grid gap-x-6 gap-y-1.5 md:grid-cols-2">
                <DefinitionList
                    items={policies.values.map((value) => ({
                        label: value.label,
                        value: value.value,
                        mono: true,
                    }))}
                />
                {policies.packageConfigs?.length > 0 && (
                    <DefinitionList
                        items={[
                            {
                                label: "Package configs",
                                value: <BadgeList items={policies.packageConfigs} variant="outline" />,
                            },
                        ]}
                    />
                )}
            </div>
        </SectionCard>
    );
}

// ---- small local helpers ----

function ChipRow({ children }) {
    return <div className="flex flex-wrap gap-1.5">{children}</div>;
}

function BadgeList({ items, variant }) {
    if (!items || items.length === 0) return null;
    return (
        <ChipRow>
            {items.map((item) => (
                <Badge key={item} variant={variant}>
                    {item}
                </Badge>
            ))}
        </ChipRow>
    );
}

function SubLabel({ children, className }) {
    return (
        <div
            className={
                "mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground " +
                (className || "")
            }
        >
            {children}
        </div>
    );
}

function capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export default StatusView;
